import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDebounce } from "use-debounce";

import { useRestApi } from "@/context/api";
import { useCurrentCompany } from "@/context/auth";
import { GetUsersPayload, GetUsersQueryParams } from "@/lib/dto";
import { ApiError } from "@/lib/error";
import {
  AuthMethod,
  TemporaryRoleGrantResponse,
  UserCreationDto,
  UserCreationRequest,
  UserProducts,
  UserRoleMappingUpdate,
} from "@/types/auth";

import { queryKeys } from "./queryKeys";

const SKILLPANEL_SYNC_DELAY_MS = 2000;

export const useSelfQuery = (loggedIn: boolean) => {
  const { getSelf } = useRestApi();

  return useQuery({
    queryKey: [queryKeys.SELF],
    queryFn: getSelf,
    enabled: loggedIn,
    staleTime: Infinity,
    retry: 2,
    retryDelay: 500,
  });
};

export const useUsersQuery = (queryParams: GetUsersPayload & GetUsersQueryParams) => {
  const { getUsers } = useRestApi().user;
  const currentCompany = useCurrentCompany();
  // enabled is taken out so it never reaches the query string; accountState replaces it
  const {
    email,
    teamIds,
    roleIds,
    accountState,
    cannotSignIn,
    dormantPermissions,
    dormantProduct,
    enabled,
    ...params
  } = queryParams;
  // A null or absent account state lists every state but removed; cannotSignIn keeps the blocked ones
  const payload: GetUsersPayload = {
    email,
    teamIds,
    roleIds,
    accountState,
    cannotSignIn,
    dormantPermissions,
    dormantProduct,
  };
  return useQuery({
    queryKey: [
      queryKeys.USER_LIST,
      useDebounce(payload.email, 500),
      payload.teamIds,
      payload.roleIds,
      payload.accountState,
      payload.cannotSignIn,
      payload.dormantPermissions,
      payload.dormantProduct,
      queryParams.page,
      queryParams.size,
      queryParams.sort,
      currentCompany.id,
    ],
    queryFn: () => getUsers(currentCompany.id, payload, params),
    staleTime: Infinity,
    // Keeps the current page on screen while a filter or page change loads, never across companies
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey.at(-1) === currentCompany.id
        ? keepPreviousData(previousData)
        : undefined,
  });
};

/**
 * How many accounts each view tab holds under the list's search, team, role and dormant-permissions
 * filters. Keyed under
 * the list, so whatever refreshes the list refreshes the counts too.
 */
export const useUserCountsQuery = ({
  email,
  teamIds,
  roleIds,
  dormantPermissions,
  dormantProduct,
}: Pick<
  GetUsersPayload,
  "email" | "teamIds" | "roleIds" | "dormantPermissions" | "dormantProduct"
>) => {
  const { getUserCounts } = useRestApi().user;
  const currentCompany = useCurrentCompany();
  const [debouncedEmail] = useDebounce(email, 500);
  const query = useQuery({
    queryKey: [
      queryKeys.USER_LIST,
      "counts",
      debouncedEmail,
      teamIds,
      roleIds,
      dormantPermissions,
      dormantProduct,
      currentCompany.id,
    ],
    queryFn: () =>
      getUserCounts(currentCompany.id, {
        email: debouncedEmail,
        teamIds,
        roleIds,
        dormantPermissions,
        dormantProduct,
      }),
    staleTime: Infinity,
    // The tabs keep their numbers while new ones load, never across companies
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey.at(-1) === currentCompany.id
        ? keepPreviousData(previousData)
        : undefined,
  });
  // Until the search settles, the counts are those of the term before it, as a placeholder's are
  return { ...query, isPlaceholderData: query.isPlaceholderData || debouncedEmail !== email };
};

export const useUserDetailsQuery = (
  id: string,
  options: { enabled?: boolean; retry?: boolean; staleTime?: number } = {},
) => {
  const { getUserDetails } = useRestApi().user;
  const currentCompany = useCurrentCompany();

  return useQuery({
    queryKey: [queryKeys.USER_DETAILS, id, currentCompany.id],
    queryFn: () => getUserDetails(id, currentCompany.id),
    staleTime: Infinity,
    placeholderData: keepPreviousData,
    // No account under the id is an answer, not a hiccup: its creation may still be scheduled
    retry: (failureCount, error) => !isNotFound(error) && failureCount < 3,
    ...options,
  });
};

export const isNotFound = (error: unknown) => error instanceof ApiError && error.status === 404;

/** Keyed under the user's details, so every change that refreshes them checks sign-in again. */
export const useSignInDiagnosisQuery = (userId: string) => {
  const { getSignInDiagnosis } = useRestApi().user;
  const currentCompany = useCurrentCompany();

  return useQuery({
    queryKey: [queryKeys.USER_DETAILS, userId, currentCompany.id, "signInDiagnosis"],
    queryFn: () => getSignInDiagnosis(currentCompany.id, userId),
  });
};

export const useUserProductsQuery = (userId: string) => {
  const { getUserProducts } = useRestApi().user;
  const currentCompany = useCurrentCompany();

  return useQuery({
    queryKey: [queryKeys.USER_PRODUCTS, userId, currentCompany.id],
    queryFn: () => getUserProducts(currentCompany.id, userId),
    staleTime: Infinity,
    placeholderData: keepPreviousData,
  });
};

export const useUpdateUserRolesMutation = () => {
  const { updateUserRoles } = useRestApi().user;
  const queryClient = useQueryClient();
  const currentCompany = useCurrentCompany();

  return useMutation({
    mutationFn: (args: { userId: string; roleMapping: UserRoleMappingUpdate["roles"] }) => {
      const userRoleMappingUpdate: UserRoleMappingUpdate = {
        uuid: args.userId,
        roles: args.roleMapping,
      };
      return updateUserRoles(currentCompany.id, args.userId, userRoleMappingUpdate);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.USER_LIST],
        refetchType: "active",
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.USER_DETAILS, variables.userId],
        refetchType: "active",
      });
    },
  });
};

export const useUpdateUserProductsMutation = () => {
  const { updateUserProducts } = useRestApi().user;
  const currentCompany = useCurrentCompany();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (args: { userId: string; products: UserProducts["products"] }) =>
      updateUserProducts(currentCompany.id, args.userId, { products: args.products }),
    onSuccess: (_data, variables) => {
      queryClient.setQueryData(
        [queryKeys.USER_PRODUCTS, variables.userId, currentCompany.id],
        _data,
      );
      // Which permissions are dormant follows the products, in the details and in the list
      queryClient.invalidateQueries({
        queryKey: [queryKeys.USER_LIST],
        refetchType: "active",
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.USER_DETAILS, variables.userId],
        refetchType: "active",
      });
    },
  });
};

export const useUpdateUserAuthMethodMutation = () => {
  const { updateUserAuthMethod } = useRestApi().user;
  const currentCompany = useCurrentCompany();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (args: { userId: string; authMethod: AuthMethod }) =>
      updateUserAuthMethod(currentCompany.id, args.userId, args.authMethod),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.USER_DETAILS, variables.userId],
        refetchType: "active",
      });
    },
  });
};

export const useUpdateUserEnabledMutation = () => {
  const { updateUserEnabled } = useRestApi().user;
  const currentCompany = useCurrentCompany();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (args: { userId: string; enabled: boolean }) =>
      updateUserEnabled(currentCompany.id, args.userId, args.enabled),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.USER_DETAILS, variables.userId],
        refetchType: "active",
      });
    },
  });
};

export const useUpdateUserHiddenInSkillPanelMutation = () => {
  const { updateUserHiddenInSkillPanel } = useRestApi().user;
  const currentCompany = useCurrentCompany();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (args: { userId: string; hidden: boolean }) =>
      updateUserHiddenInSkillPanel(currentCompany.id, args.userId, args.hidden),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.USER_DETAILS, variables.userId],
        refetchType: "active",
      });
      // SkillPanel applies the flag from an event, so asking it right away reads the old state.
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.SKILLPANEL_VISIBILITY, currentCompany.id, variables.userId],
          refetchType: "active",
        });
      }, SKILLPANEL_SYNC_DELAY_MS);
    },
  });
};

export const useUpdateUserCustomFieldsMutation = () => {
  const { updateUserCustomFields } = useRestApi().user;
  const currentCompany = useCurrentCompany();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (args: { userId: string; values: Record<string, string> }) =>
      updateUserCustomFields(currentCompany.id, args.userId, args.values),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.USER_DETAILS, variables.userId],
        refetchType: "active",
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.USER_CHANGES, currentCompany.id, variables.userId],
      });
    },
  });
};

export const useUnlockUserAccountMutation = () => {
  const { unlockUserAccount } = useRestApi().user;
  const currentCompany = useCurrentCompany();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => unlockUserAccount(currentCompany.id, userId),
    onSuccess: (_data, userId) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.USER_DETAILS, userId],
        refetchType: "active",
      });
    },
  });
};

export const useAdminResetPasswordMutation = () => {
  const { adminResetPassword } = useRestApi().user;

  return useMutation({
    mutationFn: (userId: string) => adminResetPassword(userId),
  });
};

export const useChangeUserEmailMutation = () => {
  const { changeUserEmail } = useRestApi().user;
  const currentCompany = useCurrentCompany();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (args: { userId: string; newEmail: string }) =>
      changeUserEmail(currentCompany.id, args.userId, args.newEmail),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.USER_DETAILS, variables.userId],
        refetchType: "active",
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.USER_LIST],
        refetchType: "active",
      });
    },
  });
};

export const useBulkDeleteUsersMutation = () => {
  const { bulkDeleteUsers } = useRestApi().user;
  const queryClient = useQueryClient();
  const currentCompany = useCurrentCompany();

  return useMutation({
    mutationFn: (args: { userIds: string[]; reason?: string }) =>
      bulkDeleteUsers(currentCompany.id, args.userIds, args.reason),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.USER_LIST],
        refetchType: "active",
      });
    },
  });
};

/** How often a scheduled creation is checked while its panel is open. */
const CREATION_POLL_MS = 5000;

const SCHEDULED_STATUSES: UserCreationDto["status"][] = ["PENDING", "PROCESSING", "CHANGED"];

/** The import has not picked the row up yet, so the account does not exist. */
export const isCreationScheduled = (creation: UserCreationDto) =>
  SCHEDULED_STATUSES.includes(creation.status);

/** The import created the account, so its details can be shown in place of the scheduled one. */
export const isCreationDone = (creation: UserCreationDto) =>
  creation.status === "COMPLETED" &&
  (creation.action === "CREATED" || creation.action === "UPDATED");

export const useCreateUserMutation = () => {
  const { createUser } = useRestApi().user;
  const currentCompany = useCurrentCompany();

  return useMutation({
    mutationFn: (request: UserCreationRequest) => createUser(currentCompany.id, request),
  });
};

/**
 * The creation an admin scheduled for the account `userId` will have. Checked again every few seconds
 * while the import has still to run; once it has, the account's details and the list are refreshed,
 * so both show the new account.
 */
export const useUserCreationQuery = (userId: string, options: { enabled?: boolean } = {}) => {
  const { getUserCreation } = useRestApi().user;
  const currentCompany = useCurrentCompany();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [queryKeys.USER_CREATION, userId, currentCompany.id],
    queryFn: async () => {
      const creation = await getUserCreation(currentCompany.id, userId);
      if (isCreationDone(creation)) {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [queryKeys.USER_DETAILS, userId, currentCompany.id],
          }),
          queryClient.invalidateQueries({ queryKey: [queryKeys.USER_LIST] }),
        ]);
      }
      return creation;
    },
    refetchInterval: (query) =>
      query.state.data && isCreationScheduled(query.state.data) ? CREATION_POLL_MS : false,
    retry: false,
    ...options,
  });
};

export const useUserCreationContextQuery = () => {
  const { getUserCreationContext } = useRestApi().user;
  const currentCompany = useCurrentCompany();

  return useQuery({
    queryKey: [queryKeys.USER_CREATION_CONTEXT, currentCompany.id],
    queryFn: () => getUserCreationContext(currentCompany.id),
    staleTime: 60_000,
  });
};

export const useTemporaryRolesQuery = (userId: string, enabled: boolean) => {
  const { getTemporaryRoles } = useRestApi().user;
  const currentCompany = useCurrentCompany();

  return useQuery<TemporaryRoleGrantResponse[]>({
    queryKey: [queryKeys.TEMPORARY_ROLES, userId, currentCompany.id],
    queryFn: () => getTemporaryRoles(currentCompany.id, userId),
    enabled,
    refetchInterval: 60_000,
  });
};

export const useGrantTemporaryRoleMutation = () => {
  const { grantTemporaryRole } = useRestApi().user;
  const queryClient = useQueryClient();
  const currentCompany = useCurrentCompany();

  return useMutation({
    mutationFn: (args: {
      userId: string;
      application: string;
      roleName: string;
      durationMinutes: number;
    }) =>
      grantTemporaryRole(currentCompany.id, args.userId, {
        application: args.application,
        roleName: args.roleName,
        durationMinutes: args.durationMinutes,
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.TEMPORARY_ROLES, variables.userId],
        refetchType: "active",
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.USER_DETAILS, variables.userId],
        refetchType: "active",
      });
    },
  });
};

export const useRevokeTemporaryRoleMutation = () => {
  const { revokeTemporaryRole } = useRestApi().user;
  const queryClient = useQueryClient();
  const currentCompany = useCurrentCompany();

  return useMutation({
    mutationFn: (args: { userId: string; grantId: number }) =>
      revokeTemporaryRole(currentCompany.id, args.userId, args.grantId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.TEMPORARY_ROLES, variables.userId],
        refetchType: "active",
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.USER_DETAILS, variables.userId],
        refetchType: "active",
      });
    },
  });
};
